/**
 * VersionSwitcher — floating pill in the top bar that lets you
 * toggle between pitch deck content versions.
 */
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GitBranch, Check, ChevronDown } from "lucide-react";
import versions from "../../data/pitchDeckVersions";

const versionList = Object.values(versions);

export default function VersionSwitcher({ current, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const currentVersion = versions[current];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 transition-colors"
      >
        <GitBranch className="w-3.5 h-3.5 text-[#A78BFA]" />
        <span className="hidden sm:inline">{currentVersion?.label || current}</span>
        <span className="sm:hidden">{current}</span>
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-72 max-w-[90vw] bg-[#1A2332] border border-white/15 rounded-xl shadow-2xl overflow-hidden z-50"
          >
            <div className="px-3 py-2 border-b border-white/10">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Content Version</p>
            </div>
            <div className="py-1">
              {versionList.map((v) => (
                <button
                  key={v.key}
                  onClick={() => { onChange(v.key); setOpen(false); }}
                  className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-white/5 transition-colors ${
                    current === v.key ? "bg-white/[0.03]" : ""
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center mt-0.5 flex-shrink-0 ${
                    current === v.key
                      ? "border-[#4FA9A1] bg-[#4FA9A1]/20"
                      : "border-gray-600"
                  }`}>
                    {current === v.key && <Check className="w-2.5 h-2.5 text-[#4FA9A1]" />}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${current === v.key ? "text-white" : "text-gray-300"}`}>
                      {v.label}
                    </p>
                    <p className="text-[11px] text-gray-400 leading-snug mt-0.5">{v.description}</p>
                  </div>
                </button>
              ))}
            </div>
            <div className="px-3 py-2 border-t border-white/10">
              <p className="text-[10px] text-gray-400">
                Add versions in <span className="font-mono text-gray-400">src/data/</span>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
